import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Dropdown } from "../../../../components/dropdown/Dropdown";
import { MultiSelectDropdown } from "../../../../components/dropdown/MultiSelectDropdown";
import {
  createAccessZone,
  updateAccessZone,
  getAccessZoneById,
} from "../../../../service/securityService";
import {
  AccessZone,
  AccessZoneFormData,
  AccessLevel,
} from "../../../../types/security_types";
import { usePremisesStore, useRoleManageStore } from "../../../../store/store";

interface AccessZoneFormProps {
  zone?: AccessZone;
  onSuccess: () => void;
  onCancel: () => void;
  isView?: boolean;
}

const AccessZoneForm: React.FC<AccessZoneFormProps> = ({
  zone,
  onSuccess,
  onCancel,
  isView = false,
}) => {
  const isEdit = !!zone;
  const { allPremises, fetchPremises } = usePremisesStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<AccessZoneFormData>({
    zoneName: "",
    premise: "",
    premiseId: "",
    accessLevel: "Restricted",
    allowedRoles: [],
    active: true,
  });

  useEffect(() => {
    fetchPremises();
    fetchAllRoles();
    if (isEdit && zone) {
      loadZoneData();
    }
  }, [zone, fetchPremises, fetchAllRoles]);

  const loadZoneData = async () => {
    if (!zone) return;
    try {
      setLoading(true);
      const id = zone._id || zone.id;
      if (!id) return;
      const zoneData = await getAccessZoneById(id);
      setFormData({
        zoneName: zoneData.zoneName || "",
        premise: zoneData.premise || "",
        premiseId: zoneData.premiseId || "",
        accessLevel: zoneData.accessLevel || "Restricted",
        allowedRoles: zoneData.allowedRoles || [],
        active: zoneData.active !== undefined ? zoneData.active : true,
      });
    } catch (error: any) {
      console.error("Error loading zone data:", error);
      toast.error("Failed to load zone data");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.zoneName.trim()) {
      newErrors.zoneName = "Zone name is required";
    }

    if (!formData.premiseId) {
      newErrors.premise = "Premise is required";
    }

    if (!formData.accessLevel) {
      newErrors.accessLevel = "Access level is required";
    }

    if (!formData.allowedRoles || formData.allowedRoles.length === 0) {
      newErrors.allowedRoles = "At least one role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);
      if (isEdit && zone) {
        const id = zone._id || zone.id;
        if (!id) return;
        await updateAccessZone(id, formData);
        toast.success("Access zone updated successfully");
      } else {
        await createAccessZone(formData);
        toast.success("Access zone created successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Error saving access zone:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to save access zone",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePremiseChange = (premise: string) => {
    const selectedPremise = allPremises.find((p: any) => p._id === premise);
    setFormData({
      ...formData,
      premise: selectedPremise?.premisesName || "",
      premiseId: premise,
    });
    setErrors({ ...errors, premise: "" });
  };

  const handleRoleChange = (selectedRoles: string[]) => {
    setFormData({
      ...formData,
      allowedRoles: selectedRoles,
    });
    setErrors({ ...errors, allowedRoles: "" });
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isEdit ? "Update Access Zone Details" : "Create New Access Zone"}
      </h2>
      <form onSubmit={handleSubmit} id="accessZoneForm" className="px-6 pb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Zone Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              disabled={isView}
              value={formData.zoneName}
              onChange={(e) => {
                setFormData({ ...formData, zoneName: e.target.value });
                setErrors({ ...errors, zoneName: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.zoneName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter zone name"
              required
            />
            {errors.zoneName && (
              <span className="text-red-500 text-xs">{errors.zoneName}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Premise <span className="text-red-500">*</span>
            </label>
            <Dropdown
              disabled={isView}
              selectedValue={formData.premiseId || "ALL Premises"}
              onChange={handlePremiseChange}
              type="premises"
            />
            {errors.premise && (
              <span className="text-red-500 text-xs">{errors.premise}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Access Level <span className="text-red-500">*</span>
            </label>
            <select
              disabled={isView}
              value={formData.accessLevel}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  accessLevel: e.target.value as AccessLevel,
                });
                setErrors({ ...errors, accessLevel: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.accessLevel ? "border-red-500" : "border-gray-300"
              }`}
              required
            >
              <option value="Public">Public</option>
              <option value="Restricted">Restricted</option>
              <option value="Critical">Critical</option>
            </select>
            {errors.accessLevel && (
              <span className="text-red-500 text-xs">{errors.accessLevel}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Allowed Roles <span className="text-red-500">*</span>
            </label>
            <MultiSelectDropdown
              options={rolesData.map((role: any) => ({
                value: role._id,
                label: role.roleName || role.userRole,
              }))}
              disabled={isView}
              selectedValues={formData.allowedRoles}
              onChange={handleRoleChange}
              placeholder="Select roles"
            />
            {errors.allowedRoles && (
              <span className="text-red-500 text-xs">
                {errors.allowedRoles}
              </span>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              disabled={isView}
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
              className="w-4 h-4 text-[#0fa9a6] border-gray-300 rounded focus:ring-[#0fa9a6]"
            />
            <label htmlFor="active" className="ml-2 text-sm text-gray-700">
              Active
            </label>
          </div>
        </div>
      </form>
    </>
  );
};

export default AccessZoneForm;
